import type { Container, ContainerItem, Item, ItemSearchResult, SearchResult } from "@egalregal/shared";
import { entryPathLabelOf, filterContainers, filterItems, queryTerms, scoreItemTerms } from "@egalregal/shared";
import { parseQuery, isPureDiscrete, filterAppliesToItems } from "./queryParser";
import { cosine, sortRanked, type RankedContainer } from "./vector";
import { embeddingService } from "./embeddingService";
import { containerVectors, itemVectors as storeItemVectors, getMirroredItems, getMirroredPlacements } from "./catalogStore";
import { keywordSearch, type KeywordHit } from "./textFallback";
import { DEFAULT_SEARCH_MODE, type SearchMode } from "./searchUrl";
import { log } from "../../logger";

/**
 * Local search router (ADR-0022 + item-scan-and-search 08/09/10). Routes each
 * query to the discrete filter path and/or the free-text semantic/keyword path:
 *
 *   - filters are a hard full-scan predicate over the small catalog; `id`,
 *     `barcode` and the date keys are item-level fields, so an exact barcode or
 *     a pure deadline lookup is a pure-discrete short-circuit that never wakes
 *     the model (ticket 09, deadlines 05);
 *   - free text drives ranking — semantically when the model is healthy
 *     (containers and items on one cosine scale, ticket 10), keyword-wise
 *     (Orama) otherwise (B4/B5); items surface one result per placement, and
 *     an unplaced own item once, with no location (ticket 06/08);
 *   - a query with no free text short-circuits and never touches the model (C3).
 */

export type { SearchMode };

/**
 * Resolves a container's catalog entry display name (`Garage`,
 * `Werkstatt@bob`) from the readable union. Absent in unit tests, where the
 * plain container path is expected.
 */
export type EntryLabelOf = (containerId: string) => string | null;

/** Entry-labelled display path of a container hit (ticket 12). */
function displayPath(
  containers: Container[],
  id: string,
  entryLabelOf?: EntryLabelOf,
): string {
  return entryPathLabelOf(containers, id, entryLabelOf?.(id));
}

function toHit(
  c: Container,
  containers: Container[],
  score: number,
  entryLabelOf?: EntryLabelOf,
): SearchResult {
  return {
    type: "container",
    container: c,
    path: displayPath(containers, c.id, entryLabelOf),
    score,
  };
}

/** One item hit per placement link; an item with no placement appears once. */
function itemHitsFor(
  item: Item,
  containers: Container[],
  placements: ContainerItem[],
  score: number,
  entryLabelOf?: EntryLabelOf,
): ItemSearchResult[] {
  const homes = placements.filter((p) => p.itemId === item.id).map((p) => p.containerId);
  const ids: string[] = homes.length > 0 ? homes : [""];
  return ids.map((containerId) => {
    const container = containerId ? containers.find((c) => c.id === containerId) : undefined;
    return {
      type: "item",
      item,
      containerId: container?.id ?? "",
      containerName: container?.name ?? "",
      path: container ? displayPath(containers, container.id, entryLabelOf) : "",
      score,
    };
  });
}

/** Compute a query embedding once, then cosine-rank every candidate. */
async function semanticRankContainers(
  candidates: Container[],
  containers: Container[],
  freeText: string,
  limit: number,
  service: SearchDeps["service"],
  vectors: Map<string, Float32Array>,
  entryLabelOf?: EntryLabelOf,
): Promise<SearchResult[]> {
  if (candidates.length === 0 || !service) return [];
  const queryVec = await service.embedQuery(freeText);
  const scored: RankedContainer[] = [];
  for (const c of candidates) {
    const docVec = vectors.get(c.id);
    if (!docVec) continue;
    scored.push({
      container: c,
      path: displayPath(containers, c.id, entryLabelOf),
      score: cosine(queryVec, docVec),
    });
  }
  scored.sort(sortRanked);
  return scored.slice(0, limit).map((h) => toHit(h.container, containers, h.score, entryLabelOf));
}

/** Cosine-rank items against the query on the same scale as containers (ticket 10). */
async function semanticRankItems(
  items: Item[],
  containers: Container[],
  placements: ContainerItem[],
  query: string,
  service: SearchDeps["service"],
  vectors: Map<string, Float32Array>,
  entryLabelOf?: EntryLabelOf,
): Promise<SearchResult[]> {
  if (items.length === 0 || !service) return [];
  const queryVec = await service.embedQuery(query);
  const hits: SearchResult[] = [];
  for (const item of items) {
    const docVec = vectors.get(item.id);
    if (!docVec) continue;
    hits.push(...itemHitsFor(item, containers, placements, cosine(queryVec, docVec), entryLabelOf));
  }
  return hits.sort(compareScores(placements));
}

/**
 * Keyword item ranking (ticket 08): Orama fuzzy hits (already gated to the
 * candidate items by the router) fused with the shared precise scorer from
 * ticket 01 (higher score wins per placement), so browser and server rank item
 * free text identically. Orama builds the index over the full mirror; candidates
 * gate the precise loop only.
 */
async function keywordRankItems(
  items: Item[],
  containers: Container[],
  placements: ContainerItem[],
  freeText: string,
  limit: number,
  fuzzy: KeywordHit[],
  entryLabelOf?: EntryLabelOf,
): Promise<SearchResult[]> {
  const fused = new Map<string, ItemSearchResult>();
  const keyOf = (itemId: string, containerId: string) => `${itemId}:${containerId}`;
  const terms = queryTerms(freeText);
  const candidateIds = new Set(items.map((i) => i.id));

  for (const item of items) {
    const { score } = scoreItemTerms(item, terms);
    if (score > 0) {
      for (const hit of itemHitsFor(item, containers, placements, score, entryLabelOf)) {
        const key = keyOf(item.id, hit.containerId);
        const existing = fused.get(key);
        if (!existing || hit.score > existing.score) fused.set(key, hit);
      }
    }
  }

  for (const hit of fuzzy) {
    if (hit.docType !== "item" || !hit.item || !candidateIds.has(hit.item.id)) continue;
    for (const r of itemHitsFor(hit.item, containers, placements, hit.score, entryLabelOf)) {
      const key = keyOf(r.item.id, r.containerId);
      const existing = fused.get(key);
      if (!existing || r.score > existing.score) fused.set(key, r);
    }
  }

  return [...fused.values()].sort(compareScores(placements)).slice(0, limit);
}

/** Placement list index of an item in a container; absent means "after all". */
function placementOrder(placements: ContainerItem[], itemId: string, containerId: string): number {
  const idx = placements.findIndex((p) => p.itemId === itemId && p.containerId === containerId);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
}

/**
 * Tiebreak across merged container + item hits: score desc, then name asc,
 * then placement order asc so the first-hit placement ranks explicitly above
 * a lower-scored later placement (ticket 11) — not accidentally through Array
 * sort stability (review fix).
 */
function compareScores(placements: ContainerItem[]) {
  return (a: SearchResult, b: SearchResult): number => {
    if (a.score !== b.score) return b.score - a.score;
    const nameCmp = nameOf(a).localeCompare(nameOf(b));
    if (nameCmp !== 0) return nameCmp;
    if (a.type === "item" && b.type === "item") {
      return placementOrder(placements, a.item.id, a.containerId) - placementOrder(placements, b.item.id, b.containerId);
    }
    return 0;
  };
}

function nameOf(r: SearchResult): string {
  return r.type === "container" ? r.container.name : r.item.name;
}

/** Pure-discrete item lookup (identity fields and date keys), one hit per placement. */
function discreteItemHits(
  items: Item[],
  containers: Container[],
  placements: ContainerItem[],
  entryLabelOf?: EntryLabelOf,
): SearchResult[] {
  const hits: SearchResult[] = [];
  for (const item of items) {
    hits.push(...itemHitsFor(item, containers, placements, 1, entryLabelOf));
  }
  return hits;
}

/**
 * Test-time dependency injection seam. When omitted, `runSearch` uses the
 * shared singletons (`embeddingService`, `containerVectors`, the item mirror).
 * Tests supply fakes here so the semantic path can be exercised
 * deterministically without mocking the model-loading singleton.
 */
export interface SearchDeps {
  service?: { ready: Promise<boolean>; embedQuery(query: string): Promise<Float32Array> };
  vectors?: Map<string, Float32Array>;
  items?: Item[];
  itemVectors?: Map<string, Float32Array>;
  placements?: ContainerItem[];
  /** Catalog entry display name per container, from the readable union (ticket 12). */
  entryLabelOf?: EntryLabelOf;
  /**
   * Ranking engine for free text. Omitted falls back to the default keyword
   * mode; the `/search` URL decides the engine at the call site (shell-redesign
   * 09, spec §5). No persisted preference exists any more.
   */
  mode?: SearchMode;
}

/**
 * Run local search over the catalog and the mirrored item universe. Returns
 * `SearchResult[]` with container and item hits merged on one score scale.
 */
export async function runSearch(
  containers: Container[],
  query: string,
  limit = 20,
  deps: SearchDeps = {},
): Promise<SearchResult[]> {
  const service = deps.service ?? embeddingService;
  const vectors = deps.vectors ?? containerVectors;
  const itemVecs = deps.itemVectors ?? storeItemVectors;
  const items = deps.items ?? getMirroredItems();
  const placements = deps.placements ?? getMirroredPlacements();
  const entryLabelOf = deps.entryLabelOf;
  const parsed = parseQuery(query);

  const containerCandidates = filterContainers(containers, parsed.filters);
  const itemCandidates = filterItems(items, parsed.filters);

  // Pure-discrete lookup: filter only, short-circuit, never touch the model (C3).
  // Date filters are item-level too, so a deadline-only query returns its item
  // hits (with placements) and, for `expiresOn`, the matching containers.
  if (isPureDiscrete(parsed)) {
    if (parsed.filters.some(filterAppliesToItems)) {
      const all = [
        ...containerCandidates.map((c) => toHit(c, containers, 1, entryLabelOf)),
        ...discreteItemHits(itemCandidates, containers, placements, entryLabelOf),
      ];
      return all.sort((a, b) => nameOf(a).localeCompare(nameOf(b))).slice(0, limit);
    }
    return containerCandidates
      .slice(0, limit)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => toHit(c, containers, 1, entryLabelOf));
  }

  // Semantic is opt-in (ADR-0022 B5): keyword mode never resolves the model,
  // so this path touches no embedding state at all. Hybrid stays off until tuned.
  const mode = deps.mode ?? DEFAULT_SEARCH_MODE;
  if (mode !== "keyword") {
    const modelOk = await service.ready;
    if (modelOk && (containerCandidates.length > 0 || itemCandidates.length > 0)) {
      try {
        const [containersHit, itemsHit] = await Promise.all([
          semanticRankContainers(
            containerCandidates,
            containers,
            parsed.freeText,
            limit * 2,
            service,
            vectors,
            entryLabelOf,
          ),
          semanticRankItems(
            itemCandidates,
            containers,
            placements,
            parsed.freeText,
            service,
            itemVecs,
            entryLabelOf,
          ),
        ]);
        // If nothing ranked semantically (index still warming up), fall back wholesale.
        if (containersHit.length + itemsHit.length > 0) {
          return [...containersHit, ...itemsHit].sort(compareScores(placements)).slice(0, limit);
        }
      } catch (err) {
        // Implicit fallback: model or vector lookup failed — degrade to keyword (B4).
        log.warn({ err }, "[semantic-search] semantic ranking failed; using keyword fallback");
      }
    }
  }

  // Keyword fallback (or explicit keyword mode): one Orama index, items fused
  // with the shared precise scorer. Container hits need the display path.
  const keywordHits = await keywordSearch(containers, items, parsed.freeText, limit * 2, placements, {
    containers: new Set(containerCandidates.map((c) => c.id)),
    items: new Set(itemCandidates.map((i) => i.id)),
  });
  const containerHits = keywordHits
    .filter((h) => h.docType === "container" && h.container)
    .map((h) => toHit(h.container!, containers, h.score, entryLabelOf));
  const itemKeywordHits = await keywordRankItems(
    itemCandidates,
    containers,
    placements,
    parsed.freeText,
    limit * 2,
    keywordHits,
    entryLabelOf,
  );
  return [...containerHits, ...itemKeywordHits].sort(compareScores(placements)).slice(0, limit);
}